<template>
  <div class="english-learning-container">
    <!-- 头部 -->
    <div class="learning-header">
      <div class="header-left">
        <h2>🌟 英语小课堂</h2>
        <p>和AI老师一起快乐学英语！</p>
      </div>
      <div class="header-right">
        <div class="learning-stats">
          <el-tag type="success">今日学习: {{ todayDuration }}分钟</el-tag>
          <el-tag type="warning">新单词: {{ newWordsCount }}个</el-tag>
        </div>
        <el-switch
          v-model="textOnlyMode"
          active-text="文字聊天"
          inactive-text="语音播报"
          size="small"
          @change="toggleTextOnlyMode"
        />
        <el-button @click="showLearningReport" size="small" type="primary">
          学习报告
        </el-button>
      </div>
    </div>
    
    <!-- 学习模式选择 -->
    <div class="learning-modes">
      <el-radio-group v-model="currentMode" @change="switchMode">
        <el-radio-button label="conversation">自由对话</el-radio-button>
        <el-radio-button label="vocabulary">单词学习</el-radio-button>
      </el-radio-group>
    </div>
    
    <!-- 对话区域 -->
    <div class="chat-area" ref="chatContainer">
      <div v-for="(message, index) in messages" :key="index" 
           :class="['message-wrapper', message.role]">
        
        <!-- 用户消息 -->
        <div v-if="message.role === 'user'" class="user-message">
          <div class="message-content">
            <div class="text">{{ message.content }}</div>
            <div v-if="message.pronunciationScore" class="pronunciation-badge">
              发音: {{ message.pronunciationScore }}分
            </div>
          </div>
          <div class="avatar">👦</div>
        </div>
        
        <!-- AI消息 -->
        <div v-if="message.role === 'ai'" class="ai-message">
          <!-- AI消息内容 -->
          <div 
            v-if="message.content" 
            class="message-content"
            v-html="formatMessageContent(message.content)"
          ></div>
          <div 
            v-if="message.contentEn && showEnglish" 
            class="message-content english-content"
            v-html="formatMessageContent(message.contentEn)"
          ></div>
          
          <!-- 朗读按钮（仅在非教学时刻和非引导消息时显示） -->
          <el-button
            v-if="!message.isTeachingMoment && !message.isGuidance && message.contentEn && !message.pronunciationScore"
            type="primary"
            size="small"
            @click="speakText(message.contentEn)"
            :loading="isSpeaking && currentSpeakingText === message.contentEn"
            class="speak-button"
          >
            {{ isSpeaking && currentSpeakingText === message.contentEn ? '停止朗读' : '朗读' }}
          </el-button>
        </div>
      </div>
      
      <!-- 加载状态 -->
      <div v-if="isLoading" class="loading-indicator">
        <el-skeleton :rows="2" animated />
      </div>
    </div>
    
    <!-- 输入区域 -->
    <div class="input-area">
      <!-- 语音输入按钮 -->
      <el-button 
        v-if="isAsrSupported"
        :type="isRecording ? 'danger' : 'primary'"
        @click="toggleRecording"
        size="large"
        circle
      >
        <i :class="isRecording ? 'fas fa-stop' : 'fas fa-microphone'"></i>
      </el-button>
      
      <!-- 文本输入 -->
      <el-input
        v-model="inputMessage"
        :placeholder="getInputPlaceholder()"
        @keyup.enter="sendMessage"
        size="large"
        clearable
      />
      
      
      <!-- 发送按钮 -->
      <el-button 
        type="primary" 
        @click="sendMessage"
        :loading="isLoading"
        size="large"
      >
        发送
      </el-button>
    </div>
    
    <!-- 学习报告弹窗 -->
    <el-dialog v-model="showReport" title="学习报告" width="600px">
      <div class="learning-report">
        <h3>今日学习成果</h3>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="学习时长">{{ todayDuration }}分钟</el-descriptions-item>
          <el-descriptions-item label="新学单词">{{ newWordsCount }}个</el-descriptions-item>
          <el-descriptions-item label="练习句子">{{ sentencesCount }}句</el-descriptions-item>
          <el-descriptions-item label="掌握程度">{{ masteryLevel }}%</el-descriptions-item>
        </el-descriptions>
        
        <h4>明日学习建议</h4>
        <p>{{ nextDayRecommendation }}</p>
        
        <h4>薄弱环节</h4>
        <el-tag v-for="(point, idx) in weakPoints" :key="idx" type="danger">
          {{ point }}
        </el-tag>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { 
  createEnglishSession,
  getDailyLearningRecommendation,
  endEnglishSession,
  englishLearningStreamChatWithVoice
} from '@/services/modules/englishLearning'
import type { EnglishLearningStreamChunk } from '@/services/modules/englishLearning'
import asrService from '@/services/modules/asrService'

// 状态变量
const currentMode = ref('conversation')
const inputMessage = ref('')
const messages = ref<Array<any>>([])
const isLoading = ref(false)
const isRecording = ref(false)
const isAsrSupported = ref(asrService.isRecordingSupported())
const isSpeaking = ref(false)
const currentSpeakingText = ref('')
const showEnglish = ref(true) // 控制是否显示英文内容
const textOnlyMode = ref(false) // 控制是否为纯文字聊天模式

// 添加对话上下文缓存（用于向后端传递最近3轮对话）
const conversationContext = ref<Array<{role: string, content: string}>>([])

// 用户信息（可以从用户配置或API获取）

// 定义学习模式类型
type LearningMode = 'conversation' | 'vocabulary'


// 学习统计
const todayDuration = ref(0)
const newWordsCount = ref(0)
const sentencesCount = ref(0)
const masteryLevel = ref(0)
const nextDayRecommendation = ref('')
const weakPoints = ref<string[]>([])

// 会话信息
const sessionId = ref<number | null>(null)
const chatContainer = ref<HTMLElement | null>(null)
const showReport = ref(false)

// 学习计时器
let learningTimer: number | null = null
let startTime: number = Date.now()

// PCMPlayer 类实现（复用ChildChatView的实现）
class PCMPlayer {
  audioCtx: AudioContext
  sampleRate: number
  channels: number
  nextStartTime: number
  bufferQueue: any[]
  isPlaying: boolean
  isProcessing: boolean // 添加处理标志

  constructor(sampleRate = 24000, channels = 1) {
    // 使用类型断言处理 webkitAudioContext 不存在的 TypeScript 错误
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    this.audioCtx = new AudioContext();
    this.sampleRate = sampleRate;
    this.channels = channels;
    this.nextStartTime = 0;
    this.bufferQueue = [];
    this.isPlaying = false;
    this.isProcessing = false; // 初始化处理标志
  }

  // 将 Base64 PCM 解码为 AudioBuffer
  async decodePCM(base64Data: string) {
    const binary = atob(base64Data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    
    // 假设 16bit 小端 PCM
    const samples = new Int16Array(bytes.buffer);
    const floatSamples = new Float32Array(samples.length);
    
    // 16bit 转 float (-1.0 到 1.0)
    for (let i = 0; i < samples.length; i++) {
      floatSamples[i] = samples[i] / 32768.0;
    }

    const audioBuffer = this.audioCtx.createBuffer(
      this.channels, 
      floatSamples.length / this.channels, 
      this.sampleRate
    );
    
    const channelData = audioBuffer.getChannelData(0);
    channelData.set(floatSamples);
    
    return audioBuffer;
  }

  async playChunk(base64Data: string) {
    // 将音频数据加入队列
    this.bufferQueue.push(base64Data);
    
    // 如果当前没有在处理队列，开始处理
    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  // 处理队列中的音频块
  async processQueue() {
    if (this.bufferQueue.length === 0) {
      this.isProcessing = false;
      return;
    }
    
    this.isProcessing = true;
    
    // 取出第一个音频块
    const base64Data = this.bufferQueue.shift();
    if (!base64Data) {
      this.isProcessing = false;
      return;
    }
    
    try {
      console.log('【英语学习】开始解码音频块，长度:', base64Data.length, '时间戳:', Date.now());
      
      // 解码音频
      const audioBuffer = await this.decodePCM(base64Data);
      
      console.log('【英语学习】音频块解码成功，时长:', audioBuffer.duration.toFixed(3), '秒，时间戳:', Date.now());
      
      // 创建音频源
      const source = this.audioCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.audioCtx.destination);
      
      // 计算播放时间
      const currentTime = this.audioCtx.currentTime;
      if (this.nextStartTime < currentTime) {
        this.nextStartTime = currentTime;
      }
      
      console.log('【英语学习】准备播放音频块，计划开始时间:', this.nextStartTime.toFixed(3), '当前时间:', currentTime.toFixed(3));
      
      // 播放音频
      source.start(this.nextStartTime);
      this.nextStartTime += audioBuffer.duration;
      
      console.log('【英语学习】音频块播放已启动，结束时间:', this.nextStartTime.toFixed(3));
      
      // 监听播放完成事件
      source.onended = () => {
        console.log('【英语学习】音频块播放完成，实际时长:', audioBuffer.duration.toFixed(3), '秒');
      };
      
      // 继续处理下一个
      this.processQueue();
    } catch (error) {
      console.error('【英语学习】播放音频块失败:', error, '时间戳:', Date.now());
      // 出错后继续处理下一个
      this.processQueue();
    }
  }

  // 重置播放状态
  reset() {
    this.nextStartTime = 0;
    this.bufferQueue = [];
    this.isPlaying = false;
    this.isProcessing = false;
  }

  // 停止所有播放
  stop() {
    if (this.audioCtx.state !== 'closed') {
      this.audioCtx.close();
    }
    this.reset();
  }
}

// 创建 PCMPlayer 实例
const pcmPlayer = new PCMPlayer(24000, 1);

// 全局变量用于跟踪音频块
let audioChunkCount = 0;
let lastAudioChunkTime = 0;

// 音频数据回调处理函数 - 使用 PCMPlayer 直接播放
const onAudioChunk = (audioData: string) => {
  audioChunkCount++;
  const currentTime = Date.now();
  
  // audioData 已经是 Base64 字符串，直接使用
  const base64Data = audioData;
  
  console.log('【英语学习】收到音频数据块 #', audioChunkCount, '长度:', base64Data.length, '时间戳:', currentTime);
  
  // 计算与上一个音频块的时间间隔
  if (lastAudioChunkTime > 0) {
    const interval = currentTime - lastAudioChunkTime;
    console.log('【英语学习】与上一个音频块的时间间隔:', interval, '毫秒');
  }
  lastAudioChunkTime = currentTime;
  
  // 确保 AudioContext 处于运行状态
  if (pcmPlayer.audioCtx.state === 'suspended') {
    console.log('【英语学习】恢复 AudioContext');
    pcmPlayer.audioCtx.resume();
  }
  
  // 使用 PCMPlayer 直接播放音频块
  pcmPlayer.playChunk(base64Data).catch(error => {
    console.error('【英语学习】播放音频块失败:', error);
  });
};

// 在流式对话完成时重置计数器
const onStreamComplete = () => {
  console.log('【英语学习】流式对话完成，总共收到', audioChunkCount, '个音频块');
  audioChunkCount = 0;
  lastAudioChunkTime = 0;
};

onMounted(() => {
  initSession()
  startLearningTimer()
  loadDailyRecommendation()
  
  // 添加点击事件监听器，用于激活 AudioContext
  const activateAudio = () => {
    if (pcmPlayer.audioCtx.state === 'suspended') {
      console.log('【英语学习】用户交互激活 AudioContext');
      pcmPlayer.audioCtx.resume();
    }
    // 移除监听器，只需激活一次
    document.removeEventListener('click', activateAudio);
  };
  document.addEventListener('click', activateAudio);
})

onUnmounted(() => {
  stopLearningTimer()
  if (sessionId.value) {
    endSession()
  }
  // 清理 PCMPlayer
  pcmPlayer.stop();
})

// 初始化会话
const initSession = async () => {
  try {
    console.log('开始创建英语学习会话...');
    const response = await createEnglishSession({
      childAge: 8,
      learningMode: 'beginner'
    })
    console.log('创建会话响应:', response);
    
    if (!response || !response.id) {
      throw new Error('会话创建响应格式错误: 缺少id字段');
    }
    
    sessionId.value = response.id
    console.log('会话创建成功，sessionId:', sessionId.value);
    
    // 添加欢迎消息
    messages.value.push({
      role: 'ai',
      content: '你好呀！我是你的英语老师。我们可以用中文交流，但我会教你正确的英语表达。准备好了吗？',
      contentEn: 'Hello! I am your English teacher.',
      isTeachingMoment: false
    })
  } catch (error: any) {
    console.error('创建学习会话失败:', error);
    console.error('错误详情:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    ElMessage.error('创建学习会话失败: ' + (error.message || '未知错误'));
  }
}

// 加载每日学习建议
const loadDailyRecommendation = async () => {
  try {
    const response = await getDailyLearningRecommendation()
    if (response) {
      // 更新学习统计数据
      if (response.progress) {
        todayDuration.value = response.progress.totalDuration || 0
        newWordsCount.value = response.progress.newWordsLearned || 0
        sentencesCount.value = response.progress.sentencesPracticed || 0
        masteryLevel.value = Math.round(response.progress.masteryLevel || 0)
      }
      
      // 更新学习建议
      if (response.nextRecommendation) {
        nextDayRecommendation.value = response.nextRecommendation
      }
      
      // 更新薄弱环节
      if (response.weakPoints && Array.isArray(response.weakPoints)) {
        weakPoints.value = response.weakPoints
      } else {
        // 如果没有薄弱环节数据，提供默认建议
        weakPoints.value = ['继续努力学习！']
      }
    }
  } catch (error) {
    console.warn('加载学习建议失败:', error)
    // 保持默认值
  }
}

// 发送消息（文本输入）
const sendMessage = async () => {
  if (!inputMessage.value.trim() || !sessionId.value) return
  
  const userMessage = inputMessage.value.trim()
  inputMessage.value = ''
  
  // 添加用户消息到显示列表
  messages.value.push({
    role: 'user',
    content: userMessage
  })
  
  // 更新对话上下文（只保留最近3轮）
  conversationContext.value.push({role: 'user', content: userMessage})
  if (conversationContext.value.length > 6) { // 3轮对话 = 6条消息（用户+AI各3条）
    conversationContext.value.shift()
  }
  
  scrollToBottom()
  
  try {
    isLoading.value = true
    
    // 添加AI回复占位符
    messages.value.push({
      role: 'ai',
      content: '',
      contentEn: ''
    })
    
    const aiMessageIndex = messages.value.length - 1
    
    console.log('【DEBUG】准备调用englishLearningStreamChatWithVoice')
    console.log('【DEBUG】sessionId:', sessionId.value)
    console.log('【DEBUG】userMessage:', userMessage)
    console.log('【DEBUG】currentMode:', currentMode.value)
    console.log('【DEBUG】textOnlyMode:', textOnlyMode.value)
    console.log('【DEBUG】token:', localStorage.getItem('token'))
    
    // 使用集成流式服务，传递当前学习模式、对话上下文和文字模式标志
    const ws = await englishLearningStreamChatWithVoice(
      sessionId.value,
      userMessage,
      8, // childAge
      'beginner', // englishLevel
      currentMode.value, // learningMode
      conversationContext.value, // 传递对话上下文
      textOnlyMode.value, // 传递文字聊天模式标志
      (chunk: EnglishLearningStreamChunk) => {
        // 处理流式文本
        if (aiMessageIndex >= 0 && aiMessageIndex < messages.value.length) {
          // 分离中英文内容
          const currentContent = messages.value[aiMessageIndex].content + chunk.text
          
          // 简单的中英文分离逻辑（基于换行符）
          if (currentContent.includes('\n\n')) {
            const parts = currentContent.split('\n\n', 2)
            messages.value[aiMessageIndex].content = parts[0]
            messages.value[aiMessageIndex].contentEn = parts[1] || ''
          } else {
            messages.value[aiMessageIndex].content = currentContent
          }
          
          if (chunk.isComplete) {
            messages.value[aiMessageIndex].content = messages.value[aiMessageIndex].content.trim()
            messages.value[aiMessageIndex].contentEn = messages.value[aiMessageIndex].contentEn.trim()
            
            // 自由对话和单词学习模式下，自动播放英文内容（仅在非文字聊天模式下）
            if ((currentMode.value === 'conversation' || currentMode.value === 'vocabulary') 
                && messages.value[aiMessageIndex].contentEn && !textOnlyMode.value) {
              autoSpeakEnglish(messages.value[aiMessageIndex].contentEn)
            }
            
            // 将AI回复添加到对话上下文
            if (messages.value[aiMessageIndex].contentEn) {
              conversationContext.value.push({
                role: 'ai', 
                content: messages.value[aiMessageIndex].contentEn
              })
              if (conversationContext.value.length > 6) {
                conversationContext.value.shift()
              }
            }
          }
          
          scrollToBottom()
        }
      },
      onAudioChunk,
      () => {
        // 完成回调
        isLoading.value = false
        console.log('【DEBUG】流式对话完成')
        onStreamComplete() // 添加这行来重置音频计数器
      },
      (error: string) => {
        // 错误回调
        console.error('【DEBUG】流式对话错误:', error)
        ElMessage.error(error)
        isLoading.value = false
        // 清理AI占位符
        if (messages.value.length > 0 && messages.value[messages.value.length - 1].role === 'ai') {
          messages.value.pop()
        }
      }
    )
    
    if (!ws) {
      console.error('【DEBUG】WebSocket连接失败，返回null')
      throw new Error('WebSocket连接失败')
    }
    
  } catch (error: any) {
    console.error('【DEBUG】发送消息异常:', error)
    ElMessage.error('发送消息失败，请重试')
    isLoading.value = false
    // 清理AI占位符
    if (messages.value.length > 0 && messages.value[messages.value.length - 1].role === 'ai') {
      messages.value.pop()
    }
  }
}

// 语音输入切换
const toggleRecording = async () => {
  if (isRecording.value) {
    // 停止录音
    asrService.stopRecording()
    isRecording.value = false
    ElMessage.success('录音已停止')
  } else {
    // 开始录音
    if (!sessionId.value) {
      ElMessage.warning('请先创建学习会话')
      return
    }
    
    try {
      isRecording.value = true
      ElMessage.info('正在录音，请说话...')
      
      // 重置 PCMPlayer 状态以开始新的音频流
      pcmPlayer.reset();
      
      await asrService.startRecording(
        (result) => {
          if (result.text && result.text.trim()) {
            // 显示用户消息并自动发送（通过集成流式服务）
            inputMessage.value = result.text.trim()
            sendMessage()
          }
        },
        (error) => {
          ElMessage.error(`语音识别失败: ${error}`)
          isRecording.value = false
        },
        sessionId.value,
        8
      )
    } catch (error: any) {
      ElMessage.error('录音启动失败')
      isRecording.value = false
    }
  }
}

// 朗读文本（使用浏览器Web Speech API）
const speakText = async (text: string) => {
  if (!text || text.trim() === '') {
    ElMessage.warning('没有可朗读的文本')
    return
  }
  
  // 如果正在朗读相同的文本，停止朗读
  if (isSpeaking.value && currentSpeakingText.value === text) {
    window.speechSynthesis.cancel()
    isSpeaking.value = false
    currentSpeakingText.value = ''
    return
  }
  
  // 停止当前朗读
  if (isSpeaking.value) {
    window.speechSynthesis.cancel()
  }
  
  isSpeaking.value = true
  currentSpeakingText.value = text
  
  try {
    console.log('开始朗读文本:', text)
    
    // 创建语音合成实例
    const utterance = new SpeechSynthesisUtterance(text)
    
    // 设置语音参数
    utterance.lang = 'en-US'  // 英语
    utterance.rate = 0.9      // 语速稍慢，适合学习
    utterance.pitch = 1.0     // 正常音调
    utterance.volume = 1.0    // 最大音量
    
    // 尝试选择更好的英语语音
    const voices = window.speechSynthesis.getVoices()
    const englishVoice = voices.find(voice => 
      voice.lang.startsWith('en') && voice.name.includes('Google')
    ) || voices.find(voice => 
      voice.lang.startsWith('en-US')
    )
    
    if (englishVoice) {
      utterance.voice = englishVoice
      console.log('使用语音:', englishVoice.name)
    }
    
    // 监听朗读结束
    utterance.onend = () => {
      console.log('朗读完成')
      isSpeaking.value = false
      currentSpeakingText.value = ''
    }
    
    // 监听错误
    utterance.onerror = (event) => {
      console.error('语音播放失败:', event)
      ElMessage.error('语音播放失败')
      isSpeaking.value = false
      currentSpeakingText.value = ''
    }
    
    // 开始朗读
    window.speechSynthesis.speak(utterance)
    ElMessage.success('正在朗读...')
    
  } catch (error) {
    console.error('语音播放异常:', error)
    ElMessage.error('语音播放失败')
    isSpeaking.value = false
    currentSpeakingText.value = ''
  }
}

// 自动播放AI回复的英文内容
const autoSpeakEnglish = (text: string) => {
  // 在文字聊天模式下不自动播放
  if (textOnlyMode.value) {
    console.log('文字聊天模式下跳过自动语音播报')
    return
  }
  
  if (!text || text.trim() === '') return
  
  // 延迟500ms后自动播放，让用户先看到文字
  setTimeout(() => {
    if (!isSpeaking.value) {
      console.log('自动播放英文回复:', text.substring(0, 50) + '...')
      speakText(text)
    }
  }, 500)
}

// 结束会话
const endSession = async () => {
  if (!sessionId.value) return
  
  try {
    const response = await endEnglishSession(sessionId.value)
    if (response.sessionSummary) {
      messages.value.push({
        role: 'ai',
        content: response.sessionSummary
      })
    }
    
    ElMessage.success('学习会话已结束')
    sessionId.value = null
  } catch (error) {
    ElMessage.error('结束会话失败')
  }
}


// 模式切换函数，接受字符串参数
const switchMode = (newMode: string) => {
  console.log('【DEBUG】switchMode called with:', newMode)
  
  // 验证模式是否有效
  const validModes: string[] = ['conversation', 'vocabulary']
  if (!validModes.includes(newMode)) {
    console.error('【DEBUG】Invalid mode:', newMode)
    return
  }
  
  if (currentMode.value === newMode) {
    console.log('【DEBUG】Same mode, returning')
    return
  }
  
  // 最基本的切换：只更新模式和显示消息
  currentMode.value = newMode as LearningMode
  inputMessage.value = ''
  
  // 显示简单的切换消息
  messages.value.push({
    role: 'ai',
    content: `已切换到${newMode}模式`,
    contentEn: '',
    isTeachingMoment: false,
    isGuidance: true
  })
  
  scrollToBottom()
  
  console.log('【DEBUG】switchMode completed')
}

// 监听模式变化
watch(currentMode, (newVal, oldVal) => {
  console.log('【DEBUG】currentMode changed from', oldVal, 'to', newVal)
})

// 辅助方法
const scrollToBottom = () => {
  nextTick(() => {
    if (chatContainer.value) {
      chatContainer.value.scrollTop = chatContainer.value.scrollHeight
    }
  })
}

const showLearningReport = () => {
  showReport.value = true
}

const startLearningTimer = () => {
  startTime = Date.now()
  learningTimer = window.setInterval(() => {
    const duration = Math.floor((Date.now() - startTime) / 60000)
    todayDuration.value = duration
  }, 60000)
}

const stopLearningTimer = () => {
  if (learningTimer) {
    clearInterval(learningTimer)
    learningTimer = null
  }
}


// 格式化消息内容，支持简单的Markdown
const formatMessageContent = (content: string): string => {
  if (!content) return ''
  
  // 转义HTML
  let formatted = content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
  
  // 处理标题（以#开头的行）
  formatted = formatted.replace(/^#{1,6}\s+(.*?)$/gm, (match, title) => {
    const level = match.match(/^#+/)?.[0].length || 1
    return `<h${level}>${title}</h${level}>`
  })
  
  // 处理粗体 **text**
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
  
  // 处理斜体 *text*
  formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>')
  
  // 处理无序列表（以-或*开头的行）
  formatted = formatted.replace(/^(\s*)[-*]\s+(.*?)$/gm, '$1<li>$2</li>')
  formatted = formatted.replace(/(<li>.*?<\/li>)/gs, '<ul>$1</ul>')
  
  // 处理有序列表（以数字.开头的行）
  formatted = formatted.replace(/^(\s*)\d+\.\s+(.*?)$/gm, '$1<li>$2</li>')
  formatted = formatted.replace(/(<li>.*?<\/li>)/gs, '<ol>$1</ol>')
  
  // 处理引用块（以>开头的行）
  formatted = formatted.replace(/^>\s+(.*?)$/gm, '<blockquote>$1</blockquote>')
  
  // 处理代码块（```包围的内容）
  formatted = formatted.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
  
  // 处理行内代码（`包围的内容）
  formatted = formatted.replace(/`([^`]+)`/g, '<code>$1</code>')
  
  // 处理段落（连续的非空行）
  const lines = formatted.split('\n')
  let inParagraph = false
  let paragraphContent = ''
  const resultLines = []
  
  for (let line of lines) {
    // 检查是否是HTML标签行或空行
    const isHtmlTag = /^<(?:h[1-6]|ul|ol|li|blockquote|pre|code|strong|em|br)\b/i.test(line.trim())
    const isEmpty = line.trim() === ''
    
    if (isHtmlTag) {
      // 如果在段落中，先结束段落
      if (inParagraph && paragraphContent.trim()) {
        resultLines.push(`<p>${paragraphContent.trim()}</p>`)
        paragraphContent = ''
        inParagraph = false
      }
      resultLines.push(line)
    } else if (isEmpty) {
      // 空行结束段落
      if (inParagraph && paragraphContent.trim()) {
        resultLines.push(`<p>${paragraphContent.trim()}</p>`)
        paragraphContent = ''
        inParagraph = false
      }
      resultLines.push('')
    } else {
      // 普通文本行，加入段落
      if (!inParagraph) {
        inParagraph = true
      }
      paragraphContent += (paragraphContent ? ' ' : '') + line
    }
  }
  
  // 处理最后的段落
  if (inParagraph && paragraphContent.trim()) {
    resultLines.push(`<p>${paragraphContent.trim()}</p>`)
  }
  
  formatted = resultLines.join('\n')
  
  // 处理剩余的换行（在HTML标签之间）
  formatted = formatted.replace(/<\/(h[1-6]|p|li|blockquote)>\s*<(?!(?:h[1-6]|p|li|blockquote))/g, '</$1><br>')
  
  return formatted
}

const getInputPlaceholder = () => {
  switch (currentMode.value) {
    case 'vocabulary':
      return '输入单词或指令（如"复习"）...'
    default:
      return '输入中文或英文...'
  }
}

const toggleTextOnlyMode = (value: boolean) => {
  textOnlyMode.value = value
  ElMessage.success(value ? '已切换到文字聊天模式' : '已切换到语音播报模式')
  
  // 如果切换到文字模式，停止当前的语音播放
  if (value && isSpeaking.value) {
    window.speechSynthesis.cancel()
    isSpeaking.value = false
    currentSpeakingText.value = ''
  }
}

</script>

<style scoped>
.english-learning-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}

.learning-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.header-left h2 {
  color: #409eff;
  margin-bottom: 8px;
}

.learning-stats {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.learning-modes {
  margin-bottom: 16px;
  padding: 8px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.chat-area {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: #f9fafc;
}

.message-wrapper {
  display: flex;
  margin-bottom: 16px;
  padding: 12px;
  border-radius: 8px;
}

.user-message {
  flex-direction: row-reverse;
  background-color: #409eff;
  color: white;
}

.ai-message {
  background-color: #f0f2f5;
  color: #333;
}

.avatar {
  font-size: 24px;
  margin: 0 8px;
  align-self: flex-start;
}

.message-content {
  flex: 1;
}

.english-content {
  flex: 1;
  font-family: 'Times New Roman', 'Georgia', serif;
  font-size: 16px;
  line-height: 1.8;
  letter-spacing: 0.5px;
  color: #2c3e50;
  background-color: #f8f9fa;
  padding: 12px;
  border-left: 4px solid #409eff;
  margin-top: 8px;
  border-radius: 0 8px 8px 0;
}

/* 英文段落样式 */
.english-content p {
  margin: 8px 0;
  text-align: justify;
}

/* 英文标题样式 */
.english-content h1,
.english-content h2,
.english-content h3,
.english-content h4,
.english-content h5,
.english-content h6 {
  color: #2c3e50;
  margin: 16px 0 12px 0;
  font-weight: 600;
  line-height: 1.4;
}

/* 英文列表样式 */
.english-content ul,
.english-content ol {
  margin: 12px 0;
  padding-left: 24px;
}

.english-content li {
  margin-bottom: 8px;
  line-height: 1.6;
}

/* 英文引用样式 */
.english-content blockquote {
  margin: 16px 0;
  padding: 12px 16px;
  border-left: 4px solid #409eff;
  background-color: #f0f7ff;
  font-style: italic;
  color: #4a5568;
}

/* 英文代码或特殊文本 */
.english-content code {
  font-family: 'Courier New', monospace;
  background-color: #f1f5f9;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 14px;
}

/* 单词学习模式下的单词卡片 */
.vocabulary-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 12px;
  margin: 16px 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.vocabulary-word {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 8px;
  text-align: center;
  font-family: 'Georgia', serif;
}

.vocabulary-phonetic {
  font-size: 18px;
  margin-bottom: 12px;
  text-align: center;
  opacity: 0.9;
  font-family: 'Courier New', monospace;
}

.vocabulary-definition {
  font-size: 16px;
  line-height: 1.6;
  text-align: justify;
}

/* 发音评分卡片样式 */
.pronunciation-result-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
  color: white;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
}

.score-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0 auto 20px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 3px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.score-circle:hover {
  transform: scale(1.05);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
}

.score-circle.excellent {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  animation: pulse-glow 2s infinite;
}

.score-circle.good {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.score-circle.fair {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
}

.score-circle.needs-improvement {
  background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 20px rgba(240, 147, 251, 0.5);
  }
  50% {
    box-shadow: 0 0 40px rgba(240, 147, 251, 0.8);
  }
}

.score-number {
  font-size: 48px;
  font-weight: bold;
  line-height: 1;
}

.score-label {
  font-size: 16px;
  opacity: 0.9;
  margin-top: 4px;
}

.feedback-section {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 16px;
  backdrop-filter: blur(5px);
}

.feedback-item {
  margin-bottom: 16px;
}

.feedback-item:last-child {
  margin-bottom: 0;
}

.feedback-item strong {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  opacity: 0.95;
}

.feedback-item p {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
}

.correct-pronunciation {
  font-family: 'Courier New', monospace;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 12px;
  border-radius: 6px;
  margin: 8px 0;
  font-weight: 500;
}

.chinese-text {
  margin-bottom: 8px;
}

.english-text {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 8px;
  background-color: #e6f3ff;
  border-radius: 4px;
}

.score-section {
  margin: 12px 0;
}

.grammar-section {
  margin: 12px 0;
  padding: 12px;
  background-color: #fff8e1;
  border-radius: 4px;
}

.error-comparison {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.wrong {
  color: #f56c6c;
}

.correct {
  color: #67c23a;
}

.arrow {
  font-weight: bold;
}

.teaching-section {
  margin: 12px 0;
  padding: 12px;
  background-color: #f0f9ff;
  border-radius: 4px;
}

.input-area {
  display: flex;
  gap: 16px;
  align-items: center;
}

.loading-indicator {
  padding: 16px;
}

.learning-report h3,
.learning-report h4 {
  margin: 16px 0 8px 0;
  color: #409eff;
}
</style>
